<template>
  <Dialog
    width="1000px"
    titleAlign="left"
    :title="null"
    :subTitle="null"
    :visible="visible"
    @close="closeDialog"
    :backgroundImage="false"
    :customClass="`${createRenderType}-sub-dialog type-${props.type}`"
  >
    <div class="inner-box height-all" :class="`type-${props.type}`">
      <div class="left">
        <div class="left-content-box">
          <div class="left-title">
            <img src="@/assets/img/credit/icon-gold.png" />{{
              left_title[props.type]
            }}
          </div>
          <div class="desc">{{ left_desc[props.type] }}</div>
        </div>
        <Reference v-if="props.type === 'reference'" />
        <VocalGroup v-else :type="props.type" />
      </div>
      <div class="right">
        <div class="content-box">
          <div class="content">
            <img class="icon" src="@/assets/img/credit/icon-gold.png" />
            <div class="text">{{ $$t("credit.unlock_your_full") }}</div>
          </div>
          <div class="right-title">{{ right_desc[props.type] }}</div>
          <div
            class="info-list"
            v-for="(item, index) in listBox[props.type]"
            :key="index.toString()"
          >
            <div class="item-title">{{ item.title }}</div>
            <div class="item-desc">{{ item.desc }}</div>
          </div>
        </div>
        <div class="btn-box pointer">
          <div class="btn-black" @click="handleCheckout('year')">
            <div class="text">{{ $$t("credit.unlock_all") }}</div>
            <div class="icon">{{ $$t("credit.save20") }}</div>
            <div class="tip">{{ $$t("credit.mostpopular") }}</div>
          </div>
          <div class="btn-common pointer" @click="handleCheckout('month')">
            {{ $$t("credit.unlock_monthly_access") }}
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted } from "vue";
import { storeToRefs } from "pinia";
import useHomeDataStore from "@/store/homeDataStore.js";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
import { $$language, $$t } from "@/i18n/i18n.js";
import {
  getCreditInfo,
  checkoutCredit,
  subscribePop,
  checkSubscribe,
  upgradePreview,
  upgrade,
} from "@/api/api/apiCredit.js";
import VocalGroup from "./vocal.vue";
import { handlePayMoney } from "@/utils/pay.js";
import message from "@/components/functionCallComponent/message/message.js";
import Reference from "./reference.vue";
import { firebaseUtils } from "@/utils/analytic/firebaseUtils.js";

const createLayoutStore = useCreateLayoutStore();
const homeDataStore = useHomeDataStore();
const { renderNewsData } = storeToRefs(homeDataStore);
const { createRenderType } = storeToRefs(createLayoutStore);
const allowed_behavior = ref(0);

const props = defineProps({
  visible: {
    type: Boolean,
    default: true,
  },
  type: {
    type: String,
    default: "reference", // reference, voice, melody
  },
  source: {
    type: Object,
    default: {},
  },
});

const emits = defineEmits(["close", "doRetry", "showUpModal"]);
const isloading = ref(false);

const level = reactive("pro");
const channel = reactive("stripe");
const left_title = {
  reference: $$t("credit.generate_similar_songs"),
  voice: $$t("credit.transform_any_voice"),
  melody: $$t("credit.melody_ideas_to_songs"),
};

const left_desc = {
  reference: $$t("credit.adding_a_reference"),
  voice: $$t("credit.customize_your_tracks"),
  melody: $$t("credit.turn_your_inspiration"),
};

const right_desc = {
  reference: $$t("credit.customer_style_reference"),
  voice: $$t("credit.customer_vocal"),
  melody: $$t("credit.melody_ideas"),
};

const listBox = {
  reference: [
    {
      title: $$t("credit.personalized_style_analysis"),
      desc: $$t("credit.ai_analyzes_any"),
    },
    {
      title: $$t("credit.style_consistent_creation"),
      desc: $$t("credit.upload_any_works"),
    },
  ],
  voice: [
    {
      title: $$t("credit.unlimited_vocal_freedom"),
      desc: $$t("credit.upload_any_vocal"),
    },
    {
      title: $$t("credit.perfect_ai_mixing "),
      desc: $$t("credit.smart_technology_ensures"),
    },
  ],
  melody: [
    {
      title: $$t("credit.instant_melody_transform"),
      desc: $$t("credit.simply_sing_the_melody"),
    },
    {
      title: $$t("credit.from_inspiration_to_reality"),
      desc: $$t("credit.perfect_for_capturing_spontaneous"),
    },
  ],
};

const click_name = {
  month: {
    reference: "audioref_subcribepage_monthlypay",
    voice: "audioref_subcribepage",
    melody: "melodyideas_subcribepage_monthlypay",
  },
  year: {
    reference: "audioref_subcribepage_yearlypay",
    voice: "audioref_subcribepage",
    melody: "melodyideas_subcribepage_yearlypay",
  },
};

const view_name = {
  reference: "audioref_subcribepage",
  voice: "audioref_subcribepage",
  melody: "melodyideas_subcribepage",
};

const handleCheckout = async (period) => {
  firebaseUtils.logClickEvent(click_name[period][props.type] ?? "");
  isloading.value = true;
  const {
    data: check_res,
    code: check_code,
    msg: check_msg,
  } = await checkSubscribe({ channel: channel, level: level, period: period });
  const product_id = check_res?.product?.product_id ?? "";
  const { membership_source1, membership_source2 } = props.source;
  const data = {
    product_id: product_id,
    channel,
    membership_source1,
    membership_source2,
  };
  if (check_code == 200) {
    allowed_behavior.value = check_res?.action_type;
    if (check_res?.action_type == 2 || check_res?.action_type == 3) {
      upgradePreviewFunc(product_id);
    } else if (check_res?.action_type == 1) {
      isloading.value = false;
      checkout(data);
    } else {
      isloading.value = false;
      message.error({
        content: "Switch to this plan is currently not supported",
      });
    }
  } else {
    isloading.value = false;
    codeDel(check_code, check_msg);
  }
};

const upgradePreviewFunc = async (product_id) => {
  console.log("product_id", product_id);
  let { data, code, msg } = await upgradePreview({
    new_product_id: product_id, //'prod_Qpn3lgWM7E1Clc',
    action_type: allowed_behavior.value,
  });
  isloading.value = false;
  if (code == 200) {
    emits("showUpModal", data, allowed_behavior.value);
  } else {
    message.error({
      content: msg,
    });
  }
};

const checkout = (data) => {
  checkoutCredit(data).then((res) => {
    if (res.code == 200) {
      const { order_id } = res.data;
      if (order_id) {
        handlePay(order_id);
      }
    } else {
      codeDel(res.code, res.msg);
    }
  });
};

const codeDel = (code, msg) => {
  const errorCodes = [8004, 8006, 8007, 8009, 8010, 8011, 8012, 8017];

  if (errorCodes.includes(code)) {
    message.error({
      content: $$t(`credit.errorCodesMsg_${code}`),
    });
  } else {
    message.error({
      content: msg,
    });
  }
};

const handlePay = (order_id) => {
  handlePayMoney(
    { order_id, isCredit: true },
    () => {
      paySuccess();
    },
    () => {
      payFail();
    }
  );
};

const payFail = () => {
  Router.push({
    name: "resultFail",
  });
};

const paySuccess = () => {
  Router.push({
    name: "resultCredit",
    query: {
      level: level,
    },
  });
};

const closeDialog = () => {
  firebaseUtils.logClickEvent(
    props.type == "reference"
      ? "audioref_subcribepage_close"
      : "melodyideas_subcribepage_close"
  );
  emits("close");
};

onMounted(() => {
  firebaseUtils.logViewEvent(view_name[props.type] ?? "", {});
});
</script>

<style lang="scss">
.pc-sub-dialog {
  .dialog-content {
    padding: 0 40px 40px 40px;
    color: #fff;
    .inner-box {
      display: flex;
      flex-direction: row;
      align-items: stretch; /* 保持子项等高 */
      gap: 24px;
    }
    .left {
      width: 500px;
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      justify-content: space-evenly; /* 间距等分 */
      align-items: stretch; /* 子元素宽度自动拉伸 */
      .left-title {
        font-size: 34px;
        font-weight: bold;
        line-height: normal;
        padding: 6px 36px 0;
        margin-bottom: 8px;
        text-align: center;
        img {
          display: none;
        }
      }
      .desc {
        font-size: 14px;
        font-weight: normal;
        padding: 0 36px;
        text-align: center;
        color: rgba(255, 255, 255, 0.5);
      }
    }
    .right {
      flex: 1;
      width: 400px;
      .content {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin-bottom: 10px;
        .icon {
          width: 30px;
          height: auto;
          margin-right: 8px;
        }
        .text {
          font-size: 16px;
          font-weight: bold;
          line-height: 32px;
          display: flex;
          align-items: center;
          letter-spacing: 0em;
        }
      }
      .right-title {
        font-size: 30px;
        font-weight: bold;
        line-height: 36px;
        margin-bottom: 18px;
      }

      .info-list {
        padding: 12px;
        border: 1px solid rgba(255, 255, 255, 0.5);
        box-sizing: border-box;
        border-radius: 12px;
        margin-bottom: 12px;

        .item-title {
          font-size: 18px;
          font-weight: bold;
          line-height: normal;
          letter-spacing: 0em;
          margin-bottom: 8px;
        }
        .item-desc {
          font-size: 12px;
          font-weight: normal;
          line-height: normal;
          letter-spacing: 0.02em;
          color: rgba(255, 255, 255, 0.5);
        }
      }

      .btn-box {
        padding-top: 17px;
        .btn-black {
          border-radius: 60px;
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          background: #000000;
          height: 60px;
          margin-bottom: 12px;
          position: relative;
          gap: 8px;
          padding: 0 20px;
          .text {
            font-size: 16px;
            font-weight: bold;
            background: linear-gradient(94deg, #a2ff7d 0%, #ffe95e 100%);
            -webkit-background-clip: text; /* 将背景裁剪到文字内容 */
            -webkit-text-fill-color: transparent; /* 设置文字颜色为透明 */
            text-align: center;
            // flex-grow: 1; /* 左侧内容占据剩余空间 */
            // flex-shrink: 1; /* 允许缩小以适应容器 */
            // flex-basis: 0; /* 初始大小设为 0 */
            flex-shrink: 1; /* 允许缩小以适应空间 */
            flex-basis: auto; /* 根据内容动态宽度 */
            word-wrap: break-word; /* 内容自动换行 */
          }
          .icon {
            height: 16px;
            line-height: 14px;
            border-radius: 2px;
            box-sizing: border-box;
            border: 1px solid #ffe95e;
            padding: 0 4px;
            font-size: 10px;
            font-weight: 500;
            color: #ffe95e;
            flex-shrink: 0; /* 不允许缩小 */
            white-space: nowrap; /* 禁止换行 */
          }
          .tip {
            position: absolute;
            right: 0;
            top: -15px;
            width: 93px;
            height: 33px;
            background-image: url("@/assets/img/credit/icon-yellow.png");
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center;
            font-size: 11px;
            font-weight: 500;
            line-height: 25px;
            text-align: center;
            color: #000000;
          }
          &:hover {
            .text {
              opacity: 0.8;
            }
          }
        }
        .btn-common {
          border-radius: 60px;
          display: flex;
          justify-content: center;
          align-items: center;
          background: rgba(255, 255, 255, 0.1);
          color: #ffffff;
          font-size: 14px;
          font-weight: 500;
          height: 44px;
          &:hover {
            opacity: 0.8;
          }
        }
      }
    }
  }
}

.custom-dialog.type-reference {
  .dialog-box {
    background: linear-gradient(
      117deg,
      #1e4bb3 0%,
      #827ad8 47%,
      #072883 100%
    ) !important;
    .left {
      background: linear-gradient(310deg, #452b98 0%, #1f3992 100%);
      box-shadow: inset 0px 0px 60px 0px rgba(0, 0, 0, 0.3);
    }
  }
}
.custom-dialog.type-voice {
  .dialog-box {
    background: linear-gradient(
      117deg,
      #5325a2 0%,
      #7d6fd6 47%,
      #501269 100%
    ) !important;
    .left {
      background: linear-gradient(130deg, #542280 0%, #50239e 100%);
      box-shadow: inset 0px 0px 60px 0px rgba(0, 0, 0, 0.3);
    }
  }
}
.custom-dialog.type-melody {
  .dialog-box {
    background: linear-gradient(
      117deg,
      #953371 0%,
      #b57493 47%,
      #661238 100%
    ) !important;
    .left {
      background: linear-gradient(130deg, #792a50 0%, #953e56 100%);
      box-shadow: inset 0px 0px 60px 0px rgba(0, 0, 0, 0.3);
    }
  }
}

.custom-dialog.mobile-sub-dialog {
  // .icon-button-box {
  //   position: absolute;
  //   bottom: 0;
  // }
  .dialog-box {
    padding-top: 24px;
  }
  .dialog-close {
    top: unset;
    bottom: -60px;
    left: 50%;
    width: 40px;
    height: 40px;
    transform: translateX(-50%);
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.2);
  }
  .dialog-content {
    padding-bottom: 24px;
    .left {
      color: #fff;
      background: unset;
      box-shadow: unset;
      .left-title {
        font-size: 18px;
        font-weight: bold;
        line-height: normal;
        margin-bottom: 7px;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        img {
          display: inline-block;
          margin-right: 5px;
          width: 30px;
          //   height: 24px;
        }
        // &::before{
        //   content: '';
        //   position: absolute;
        //   left: 18px;
        //   top: 50%;
        //   transform: translateY(-50%);
        //   width: 30px;
        //   height: 24px;
        //   background-image: url('@/assets/img/credit/icon-gold.png');
        //   background-repeat: no-repeat;
        //   background-position: center;
        //   background-size: cover;
        // }
      }
      .desc {
        font-size: 12px;
        font-weight: normal;
        padding: 0 16px;
        text-align: center;
        color: rgba(255, 255, 255, 0.5);
        margin-bottom: 20px;
      }
    }
    .right {
      .content-box {
        display: none;
      }
      .btn-box {
        padding: 24px 18px 0;
        .btn-black {
          border-radius: 60px;
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          background: #000000;
          height: 50px;
          margin-bottom: 12px;
          position: relative;
          gap: 8px;
          padding: 0 12px;
          .text {
            font-size: 12px;
            font-weight: bold;
            background: linear-gradient(94deg, #a2ff7d 0%, #ffe95e 100%);
            -webkit-background-clip: text; /* 将背景裁剪到文字内容 */
            -webkit-text-fill-color: transparent; /* 设置文字颜色为透明 */
            text-align: center;
            // flex-grow: 1; /* 左侧内容占据剩余空间 */
            // flex-shrink: 1; /* 允许缩小以适应容器 */
            // flex-basis: 0; /* 初始大小设为 0 */
            flex-shrink: 1; /* 允许缩小以适应空间 */
            flex-basis: auto; /* 根据内容动态宽度 */
            word-wrap: break-word; /* 内容自动换行 */
          }
          .icon {
            height: 16px;
            border-radius: 2px;
            box-sizing: border-box;
            /* 紫色版本/黄色 */
            border: 1px solid #ffe95e;
            padding: 2px 4px;
            font-size: 10px;
            font-weight: 500;
            color: #ffe95e;
            flex-shrink: 0;
            display: flex;
            align-items: center;
          }
          .tip {
            position: absolute;
            right: 0;
            top: -15px;
            width: 93px;
            height: 33px;
            background-image: url("@/assets/img/credit/icon-yellow.png");
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center;
            font-size: 11px;
            font-weight: 500;
            line-height: 25px;
            text-align: center;
            color: #000000;
          }
          &:hover {
            .text {
              opacity: 0.8;
            }
          }
        }
        .btn-common {
          border-radius: 60px;
          display: flex;
          justify-content: center;
          align-items: center;
          background: rgba(255, 255, 255, 0.1);
          color: #ffffff;
          font-size: 12px;
          font-weight: 500;
          height: 40px;
          &:hover {
            opacity: 0.8;
          }
        }
      }
    }
  }
}
</style>
